import { store } from '../../store';
import { SSENotificationEvent } from '../../types/notifications';
import notifySoundFile from '../../assets/sounds/water-drop.mp3';

// ─── Types ────────────────────────────────────────────────────────────────────

type SSEEventHandler = (event: SSENotificationEvent) => void;
type ConnectionStateHandler = (connected: boolean) => void;

// ─── Constants ────────────────────────────────────────────────────────────────

const RECONNECT_INITIAL_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 30_000;
const RECONNECT_MULTIPLIER = 2;

// Server sends a heartbeat every 25s. If nothing arrives for this long the
// stream is silently dead (e.g. network dropped without a TCP reset) and the
// reader would otherwise hang forever looking "connected".
const HEARTBEAT_TIMEOUT_MS = 60_000;

// How long a tab may stay hidden before its stream is closed. The grace period
// avoids reconnect thrash when the user is just switching between tabs.
const HIDDEN_TAB_CLOSE_DELAY_MS = 60_000;

// ─── Audio Unlocking for iOS / Mobile ─────────────────────────────────────────

let sharedAudio: HTMLAudioElement | null = null;
let audioUnlocked = false;

if (typeof window !== 'undefined') {
  sharedAudio = new Audio(notifySoundFile);
  
  const unlockAudio = () => {
    if (sharedAudio && !audioUnlocked) {
      // Mute, play, then pause to unlock the audio element without making noise
      sharedAudio.muted = true;
      sharedAudio.play().then(() => {
        sharedAudio!.pause();
        sharedAudio!.currentTime = 0;
        sharedAudio!.muted = false; // unmute for actual playback later
      }).catch((e) => {
        // Ignore failure
      });
      audioUnlocked = true;
    }
    document.removeEventListener('click', unlockAudio);
    document.removeEventListener('touchstart', unlockAudio);
  };
  
  document.addEventListener('click', unlockAudio);
  document.addEventListener('touchstart', unlockAudio);
}

// ─── Cross-Tab Leader Election for Notification Sound ────────────────────────
// Every open tab runs its own SSE connection (see NotificationSSEProvider) and
// independently receives the same server push, so without this a single
// notification would play the chime in every open tab at once. Exactly one
// tab — whichever wins this Web Lock — is allowed to actually play it; if
// that tab closes/reloads, the browser releases the lock automatically and
// another still-open tab takes over as leader.
let isSoundLeaderTab = false;

if (typeof navigator !== 'undefined' && 'locks' in navigator) {
  navigator.locks
    .request('worklap-notification-sound-leader', () => {
      isSoundLeaderTab = true;
      // Never resolves — holds the lock for this tab's lifetime.
      return new Promise<void>(() => {});
    })
    .catch(() => {
      // Lock API present but the request itself failed — fail open (every
      // tab plays) rather than silently losing sound everywhere.
      isSoundLeaderTab = true;
    });
} else {
  // No Web Locks support in this browser — fall back to the old behavior.
  isSoundLeaderTab = true;
}

// ─── SSE Client ───────────────────────────────────────────────────────────────

class NotificationSSEClient {
  private abortController: AbortController | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private hiddenCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private _isConnected = false;
  // True until connect() is called, and again after disconnect() (logout)
  private _intentionallyClosed = true;
  // True while the stream is closed because the tab is in the background
  private _pausedForHiddenTab = false;

  private eventHandlers: Set<SSEEventHandler> = new Set();
  private connectionHandlers: Set<ConnectionStateHandler> = new Set();
  private recoveryHandlers: Set<() => void> = new Set();

  constructor() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this._handleVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this._handleOnline);
    }
  }

  get isConnected() {
    return this._isConnected;
  }

  onEvent(handler: SSEEventHandler): () => void {
    this.eventHandlers.add(handler);
    return () => this.eventHandlers.delete(handler);
  }

  onConnectionChange(handler: ConnectionStateHandler): () => void {
    this.connectionHandlers.add(handler);
    return () => this.connectionHandlers.delete(handler);
  }

  /** Called on reconnect so consumers can re-fetch missed data from REST */
  onRecovery(handler: () => void): () => void {
    this.recoveryHandlers.add(handler);
    return () => this.recoveryHandlers.delete(handler);
  }

  connect() {
    this._intentionallyClosed = false;
    this._pausedForHiddenTab = false;
    this._clearHiddenCloseTimer();
    this.reconnectAttempt = 0;
    this._openStream();
    // Tab opened in the background (e.g. middle-click) — don't hold a stream for it
    if (document.visibilityState === 'hidden') this._startHiddenCloseTimer();
  }

  disconnect() {
    this._intentionallyClosed = true;
    this._pausedForHiddenTab = false;
    this._clearHiddenCloseTimer();
    this._cleanup();
    this._setConnected(false);
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  private _setConnected(value: boolean) {
    if (this._isConnected === value) return;
    this._isConnected = value;
    this.connectionHandlers.forEach((h) => h(value));
  }

  private _cleanup() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this._clearHeartbeatTimer();
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  private _canReconnect() {
    return !this._intentionallyClosed && !this._pausedForHiddenTab;
  }

  // Retries indefinitely. Exponential backoff capped at 30s, with "equal
  // jitter" (50–100% of the step) so clients don't all reconnect in lockstep
  // after a server-side outage.
  private _scheduleReconnect() {
    if (!this._canReconnect()) return;
    const step = Math.min(
      RECONNECT_INITIAL_DELAY_MS * RECONNECT_MULTIPLIER ** this.reconnectAttempt,
      RECONNECT_MAX_DELAY_MS,
    );
    const delay = step / 2 + Math.random() * (step / 2);
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this._openStream(true);
    }, delay);
  }

  /** Drop any current/pending connection and reconnect right away with fresh backoff. */
  private _reconnectNow() {
    this.reconnectAttempt = 0;
    this._openStream(true);
  }

  private _resetHeartbeatTimer() {
    this._clearHeartbeatTimer();
    this.heartbeatTimer = setTimeout(() => {
      this.heartbeatTimer = null;
      // Aborting makes the pending read reject with AbortError, which exits
      // quietly — so we schedule the reconnect ourselves.
      this._cleanup();
      this._setConnected(false);
      this._scheduleReconnect();
    }, HEARTBEAT_TIMEOUT_MS);
  }

  private _clearHeartbeatTimer() {
    if (this.heartbeatTimer) {
      clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // ─── Tab visibility / network ─────────────────────────────────────────────

  private _startHiddenCloseTimer() {
    this._clearHiddenCloseTimer();
    this.hiddenCloseTimer = setTimeout(() => {
      this.hiddenCloseTimer = null;
      if (this._intentionallyClosed) return;
      this._pausedForHiddenTab = true;
      this._cleanup();
      this._setConnected(false);
    }, HIDDEN_TAB_CLOSE_DELAY_MS);
  }

  private _clearHiddenCloseTimer() {
    if (this.hiddenCloseTimer) {
      clearTimeout(this.hiddenCloseTimer);
      this.hiddenCloseTimer = null;
    }
  }

  private _handleVisibilityChange = () => {
    if (this._intentionallyClosed) return;

    if (document.visibilityState === 'hidden') {
      this._startHiddenCloseTimer();
      return;
    }

    this._clearHiddenCloseTimer();
    // Reopen if we closed it for being hidden, or skip the remaining backoff
    // wait if it dropped. isReconnect=true fires recovery handlers, so the
    // badge re-syncs from REST (events pushed while closed are lost).
    if (this._pausedForHiddenTab || this.reconnectTimer) {
      this._pausedForHiddenTab = false;
      this._reconnectNow();
    }
  };

  private _handleOnline = () => {
    // Any stream that survived a network change is likely dead — start fresh
    if (this._canReconnect()) this._reconnectNow();
  };

  private async _openStream(isReconnect = false) {
    this._cleanup();

    const state = store.getState();
    const token = state.auth.accessToken;
    if (!token) return;

    const baseURL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
    const url = `${baseURL}/notifications/stream`;

    const controller = new AbortController();
    this.abortController = controller;

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
        },
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      // Reset backoff on successful connection
      this.reconnectAttempt = 0;
      this._setConnected(true);
      this._resetHeartbeatTimer();

      // On reconnect, fire recovery so consumers can refetch missed notifications
      if (isReconnect) {
        this.recoveryHandlers.forEach((h) => h());
      }

      await this._readStream(response.body);
    } catch (err: any) {
      if (err?.name === 'AbortError' || err?.message?.includes('aborted') || err?.message?.includes('abort')) return; // intentional disconnect
      // A newer stream already replaced this one — let it own the reconnect
      if (this.abortController !== controller) return;
      this._clearHeartbeatTimer();
      this._setConnected(false);
      this._scheduleReconnect();
    }
  }

  private async _readStream(body: ReadableStream<Uint8Array>) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEventName = 'message';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Any bytes (heartbeats included) prove the stream is alive
        this._resetHeartbeatTimer();
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? ''; // keep partial last line

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEventName = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            const raw = line.slice(5).trim();
            this._handleEvent(currentEventName, raw);
            currentEventName = 'message'; // reset after data
          } else if (line === '') {
            // blank line = end of SSE event block — reset event name
            currentEventName = 'message';
          }
        }
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      throw err;
    } finally {
      // Rejects if the stream already errored — nothing to do about it
      reader.cancel().catch(() => {});
    }

    // Stream ended cleanly — schedule reconnect
    this._clearHeartbeatTimer();
    this._setConnected(false);
    this._scheduleReconnect();
  }

  private playNotificationSound() {
    if (!isSoundLeaderTab) return;
    try {
      if (sharedAudio) {
        sharedAudio.currentTime = 0;
        sharedAudio.play().catch(e => console.log("Audio autoplay blocked", e));
      } else {
        const audio = new Audio(notifySoundFile);
        audio.play().catch(e => console.log("Audio autoplay blocked", e));
      }
    } catch (e) {
      console.error("Audio playback failed", e);
    }
  }

  private _handleEvent(eventName: string, data: string) {
    // Connection / heartbeat events need no UI action
    if (eventName === 'connected' || eventName === 'heartbeat') return;

    if (eventName === 'notification') {
      try {
        const payload: SSENotificationEvent = JSON.parse(data);
        this.eventHandlers.forEach((h) => h(payload));
        // Play synthetic chime ONLY when a new notification is CREATED
        if (payload.action === 'CREATED') {
          this.playNotificationSound();
        }
      } catch {
        // Malformed JSON — ignore
      }
    }
  }
}

// ─── Singleton ────────────────────────────────────────────────────────────────

export const notificationSSEClient = new NotificationSSEClient();
